import { Injectable, inject } from '@angular/core';
import {Actions} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {SharedKnowledgeEffectsBase} from './knowledge.effects';
import {forkJoin, from, mergeMap, Observable, throwError, withLatestFrom} from 'rxjs';
import {ILocalPageOutputDto} from '@shared/interfaces/orga-response.interface';
import {
  KnowledgeArticleListDto,
  KnowledgeExamAnswerDto,
  KnowledgeExamDto,
  KnowledgePathListDto,
  KnowledgePathStartDto,
} from '@server-models';
import {selectKnowledgePathPagination, selectKnowledgePathStart,} from './knowledge.selectors';
import {catchError, map} from 'rxjs/operators';
import {IGetKnowledgePathByIdPayload} from '../interfaces/knowledge-path-start-id-request.interface';
import {ILocalPagination} from '@shared/interfaces/pagination.interface';
import {IAttachmentKnowledgeArticle} from '@shared/components/attachments/interfaces/attachment-knowledge-article.interface';
import {selectAttachments} from '@shared/components/attachments/store/attachments.selectors';
import {SharedKnowledgeService} from '../services/shared-knowledge.service';
import {SharedKnowledgeActions} from './knowledge.actions-type';
import {SharedPermissionsService} from "@shared/services/shared-permissions.service";
import {EBetaFeatures} from "@shared/enums/beta-features.enum";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";

@Injectable({
  providedIn: 'root',
})
export class SharedKnowledgeApiEffects extends SharedKnowledgeEffectsBase {
  private _sharedKnowledgeService = inject(SharedKnowledgeService);
  private _sharedPermissionsService = inject(SharedPermissionsService);

  constructor() {
    const actions$ = inject(Actions);
    const store = inject(Store);

    super(store, actions$);
  }

  /**
   * @name getPath
   * @description
   * Receive the action started on TeamKnowledgeEffectsBase and use the latest state from knowledge.pagination
   * to check if there is more pages available and call a function to request knowledge path data
   * @memberof SharedKnowledgeApiEffects
   * @param action$
   * @return {Observable<(ILocalPageOutputDto<KnowledgePathListDto[]> | any)>}
   */
  getPath(
    action$: Observable<ISharedLocalBodyRequest>
  ): Observable<ILocalPageOutputDto<KnowledgePathListDto[]> | any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(selectKnowledgePathPagination))),
      mergeMap(([action, paginationState]) => {
        if (action.isRefresh) {
          return this._requestGetPath(action);
        } else if (this._isLastPagePaginationState(action, paginationState)) {
          return this._requestGetPath(action);
        } else {
          return [SharedKnowledgeActions.getPathItemsPagedCancel()];
        }
      })
    );
  }

  /**
   * @name getKnowledgePathById
   * @description
   * Receive the action started on TeamKnowledgeEffectsBase and use the latest state from knowledge path using id
   * to retrieve knowledge start by id data
   * @memberof SharedKnowledgeApiEffects
   * @param action$
   * @return {Observable<KnowledgePathListDto[] | any>}
   */
  getKnowledgePathById(
    action$: Observable<IGetKnowledgePathByIdPayload>
  ): Observable<KnowledgePathListDto[] | any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(selectKnowledgePathStart))),
      mergeMap(([action]) => this._getKnowledgePathById(action))
    );
  }

  getItemsSearched(action$: Observable<{
    toSearch: string
  }>): Observable<ILocalPageOutputDto<KnowledgeArticleListDto[]> | any> {
    return action$.pipe(
      mergeMap((action) => this._requestItemsSearched(action))
    );
  }

  getArticlePage(
    action$: Observable<{ articleId: number }>
  ): Observable<IAttachmentKnowledgeArticle> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(selectAttachments))),
      mergeMap(([action]) => this._requestArticlePage(action))
    );
  }

  override getExamAnswerById(
    action$: Observable<{ examId: string; pathId: string }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap((action) => this._requestKnowledgeExamById(action))
    );
  }

  override submitExamAnswer(
    action$: Observable<{ exam: KnowledgeExamAnswerDto }>
  ): Observable<any> {
    return action$.pipe(mergeMap((action) => this._submitExamAnswer(action)));
  }

  /**
   * @name _requestItemsSearched
   * @description
   * request the data to the document service to search by text
   * @memberof SharedKnowledgeApiEffects
   * @param action
   * @param isAISummaryEnabled
   * @private
   */
  private _requestItemsSearched(action: {
    toSearch: string;
  }): Observable<ILocalPageOutputDto<KnowledgeArticleListDto[]> | any> {
    return forkJoin({
      data: this._sharedKnowledgeService.getBySearch(action.toSearch!),
      summary: this._sharedKnowledgeService.getSummaryBySearch(action.toSearch!)
    }).pipe(
      map(({ data, summary }) => {
        if (summary && summary.isSuccess === false) {
          return SharedKnowledgeActions.getItemsSearchedSuccess({ data });
        }
        return SharedKnowledgeActions.getItemsSearchedSuccess({ data, summary });
      }),
      catchError((error) =>
        [SharedKnowledgeActions.getItemsSearchedFail({ error })]
      )
    );
  }

  /**
   * @name _isLastPagePaginationState
   * @description
   * Check if there is not paging information or if the pageNumber in the action is
   * less than or equal to the total number of pages available in the paginationState
   * @memberof SharedKnowledgeApiEffects
   * @param action
   * @param state
   * @private
   * @return {boolean}
   */
  private _isLastPagePaginationState(
    action: ISharedLocalBodyRequest,
    state: ILocalPagination
  ): boolean {
    return !state || action.pagination.pageNumber <= state.totalPages;
  }

  /**
   * @name _requestGetPath
   * @description
   * Request the data to TechKnowledgeService and map the value to launch and pass throw the action need it
   * @memberof SharedKnowledgeApiEffects
   * @param action
   * @private
   * @return {Observable<ILocalPageOutputDto<KnowledgePathListDto[]> | any>}
   */
  private _requestGetPath(
    action: ISharedLocalBodyRequest
  ): Observable<ILocalPageOutputDto<KnowledgePathListDto[]> | any> {
    return this._sharedKnowledgeService.getPathItems(action.pagination, action.filters).pipe(
      map((data: ILocalPageOutputDto<KnowledgePathListDto[]>) => {
        return action.isRefresh
          ? SharedKnowledgeActions.getPathItemsPagedRefresh({ data })
          : SharedKnowledgeActions.getPathItemsPagedSuccess({ data });
      }),
      catchError((error) => {
        return throwError(() =>
          SharedKnowledgeActions.getPathItemsPagedFail({ error })
        );
      })
    );
  }

  /**
   * @name _getKnowledgePathById
   * @description
   * Request the data to TechKnowledgeService by id and map the value to launch and pass throw the action need it
   * @memberof SharedKnowledgeApiEffects
   * @param action
   * @private
   * @return {Observable<KnowledgePathStartDto | any>}
   */
  private _getKnowledgePathById(
    action: IGetKnowledgePathByIdPayload
  ): Observable<KnowledgePathStartDto | any> {
    return this._sharedKnowledgeService
      .getKnowledgePathById(action.knowledgePathId)
      .pipe(
        map((data: KnowledgePathStartDto) => {
          return action.isRefresh
            ? SharedKnowledgeActions.getPathItemsByIdStartRefresh({ data })
            : SharedKnowledgeActions.getPathItemsByIdStartSuccess({ data });
        }),
        catchError((error) => [
          SharedKnowledgeActions.getPathItemsByIdStartFail({ error }),
        ])
      );
  }

  /**
   * @name _requestArticlePage
   * @param action
   * @private
   */
  private _requestArticlePage(action: {
    articleId: number;
  }): Observable<IAttachmentKnowledgeArticle | any> {
    return this._sharedKnowledgeService.getArticle(action.articleId!).pipe(
      map((data) => SharedKnowledgeActions.getArticlePageSuccess({ data: data })),
      catchError((error) => [SharedKnowledgeActions.getArticlePageFail({ error })])
    );
  }

  private _requestKnowledgeExamById(payload: {
    examId: string;
    pathId: string;
  }): Observable<KnowledgeExamDto | any> {
    return this._sharedKnowledgeService.getExamAnswerById(payload).pipe(
      map((data) => SharedKnowledgeActions.getExamAnswerByIdSuccess({ data })),
      catchError((error) => [SharedKnowledgeActions.getExamAnswerByIdFail({ error })])
    );
  }

  private _submitExamAnswer(exam: {
    exam: KnowledgeExamAnswerDto;
  }): Observable<KnowledgeExamAnswerDto | any> {
    return this._sharedKnowledgeService.submitExamAnswer(exam.exam).pipe(
      map((data) => SharedKnowledgeActions.getExamAnswerByIdSuccess({ data })),
      catchError((error) => [SharedKnowledgeActions.getExamAnswerByIdFail({ error })])
    );
  }
}
